import { useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { calcTax, fiatToBCH, formatFiat, formatBCH, getCurrencySymbol } from '@/lib/bch';
import { cn } from '@/lib/utils';

const TIP_PRESETS = [
  { label: 'No tip', pct: 0 },
  { label: '10%', pct: 10 },
  { label: '15%', pct: 15 },
  { label: '18%', pct: 18 },
  { label: '20%', pct: 20 },
  { label: '25%', pct: 25 },
];

export function TipView() {
  const { settings, fiatAmount, discountAmount, discountLabel, setTipAmount, setPendingBCH, setView } = usePOS();
  const { data: priceData } = useBCHPrice(settings.currency);
  const discountedSubtotal = Math.max(0, fiatAmount - discountAmount);
  const { taxAmount, total: subtotalWithTax } = calcTax(discountedSubtotal, settings.taxRate);
  const sym = getCurrencySymbol(settings.currency);

  const [selectedPct, setSelectedPct] = useState<number | null>(null);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const customTip = showCustom ? (parseFloat(customInput) || 0) : 0;
  const presetTip = selectedPct !== null && !showCustom
    ? parseFloat((fiatAmount * (selectedPct / 100)).toFixed(2))
    : 0;
  const tipAmt = showCustom ? customTip : presetTip;
  const grandTotal = parseFloat((subtotalWithTax + tipAmt).toFixed(2));
  const bchTotal = priceData ? fiatToBCH(grandTotal, priceData.price) : 0;

  const handlePreset = (pct: number) => {
    setShowCustom(false);
    setCustomInput('');
    setSelectedPct(pct);
  };

  const handleCustomDigit = (d: string) => {
    if (d === '.') {
      if (customInput.includes('.')) return;
      setCustomInput((p) => p === '' ? '0.' : p + '.');
      return;
    }
    if (customInput.includes('.')) {
      const dec = customInput.split('.')[1];
      if (dec && dec.length >= 2) return;
    }
    if (!customInput.includes('.') && customInput.replace(/^0/, '').length >= 5) return;
    setCustomInput((p) => {
      if (p === '0' && d !== '.') return d;
      return p + d;
    });
  };

  const handleCharge = () => {
    if (!settings.bchAddress) { setView('settings'); return; }
    setTipAmount(tipAmt);
    setPendingBCH(bchTotal);
    setView('payment');
  };

  const handleBack = () => setView('discount');

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header */}
      <div className="text-center">
        <p className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-1">Add a Tip?</p>
        <p className="text-sm text-zinc-400">Order total: {formatFiat(fiatAmount, settings.currency)}</p>
      </div>

      {/* Order summary card */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 p-4 space-y-2">
        <div className="flex justify-between text-sm text-zinc-500 dark:text-zinc-400">
          <span>Subtotal</span>
          <span>{formatFiat(fiatAmount, settings.currency)}</span>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm font-semibold text-red-500">
            <span>Discount {discountLabel ? `(${discountLabel})` : ''}</span>
            <span>−{formatFiat(discountAmount, settings.currency)}</span>
          </div>
        )}
        {settings.taxRate > 0 && (
          <div className="flex justify-between text-sm text-zinc-500 dark:text-zinc-400">
            <span>{settings.taxName} ({settings.taxRate}%)</span>
            <span>{formatFiat(taxAmount, settings.currency)}</span>
          </div>
        )}
        {tipAmt > 0 && (
          <div className="flex justify-between text-sm font-semibold text-[#0AC18E]">
            <span>Tip</span>
            <span>+{formatFiat(tipAmt, settings.currency)}</span>
          </div>
        )}
        <div className="border-t border-dashed border-zinc-200 dark:border-zinc-700 pt-2 flex justify-between">
          <span className="text-base font-bold text-zinc-800 dark:text-zinc-100">Total</span>
          <div className="text-right">
            <p className="text-xl font-black text-zinc-900 dark:text-zinc-50">
              {formatFiat(grandTotal, settings.currency)}
            </p>
            {bchTotal > 0 && (
              <p className="text-xs font-mono text-[#0AC18E]">≈ {formatBCH(bchTotal)} BCH</p>
            )}
          </div>
        </div>
      </div>

      {/* Preset tip buttons */}
      <div className="grid grid-cols-3 gap-2.5">
        {TIP_PRESETS.map(({ label, pct }) => {
          const isSelected = !showCustom && selectedPct === pct;
          const amt = pct > 0 ? parseFloat((fiatAmount * pct / 100).toFixed(2)) : 0;
          return (
            <button
              key={pct}
              onClick={() => handlePreset(pct)}
              className={cn(
                'flex flex-col items-center justify-center py-4 rounded-2xl border-2 transition-all active:scale-95',
                isSelected
                  ? 'bg-[#0AC18E] border-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/25'
                  : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:border-[#0AC18E]/40'
              )}
            >
              <span className="text-base font-black">{label}</span>
              {amt > 0 && (
                <span className={cn('text-xs mt-0.5', isSelected ? 'text-white/80' : 'text-zinc-400')}>
                  {sym}{amt.toFixed(2)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom amount */}
      {!showCustom ? (
        <button
          onClick={() => { setShowCustom(true); setSelectedPct(null); }}
          className="w-full py-3.5 rounded-2xl border-2 border-dashed border-zinc-300 dark:border-zinc-600 text-sm font-semibold text-zinc-500 dark:text-zinc-400 hover:border-[#0AC18E]/50 transition-colors"
        >
          Enter custom amount
        </button>
      ) : (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border-2 border-[#0AC18E] p-4">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Custom Tip</p>
          <div className="flex items-baseline gap-1.5 mb-3">
            <span className="text-2xl font-medium text-zinc-400">{sym}</span>
            <span className="text-4xl font-black text-zinc-900 dark:text-zinc-50 tabular-nums">
              {customInput || '0'}
            </span>
          </div>
          {/* Mini numpad */}
          <div className="grid grid-cols-3 gap-2">
            {['1','2','3','4','5','6','7','8','9','.','0','⌫'].map((k) => (
              <button
                key={k}
                onPointerDown={(e) => {
                  e.preventDefault();
                  if (k === '⌫') setCustomInput((p) => p.slice(0, -1));
                  else handleCustomDigit(k);
                }}
                className={cn(
                  'h-12 rounded-xl text-lg font-semibold transition-all active:scale-90 select-none',
                  k === '⌫'
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-400'
                    : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-800 dark:text-zinc-100'
                )}
              >
                {k}
              </button>
            ))}
          </div>
          <button
            onClick={() => { setShowCustom(false); setCustomInput(''); setSelectedPct(0); }}
            className="mt-2 text-xs text-zinc-400 hover:text-zinc-600 w-full text-center"
          >
            Cancel custom
          </button>
        </div>
      )}

      {/* Charge button */}
      <button
        onClick={handleCharge}
        className="w-full py-5 rounded-2xl text-lg font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
      >
        Charge {formatFiat(grandTotal, settings.currency)}
      </button>

      {/* Back */}
      <button
        onClick={handleBack}
        className="w-full py-3 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
      >
        ← Back
      </button>
    </div>
  );
}
